// The working groove (SPEC F5): the style's reference pattern plus your edits,
// kept as one `Pattern` row (SPEC §8). Pure: the store and tests share it.
// Assist, never author: these only copy the style's own pattern or keep your
// edits; nothing here writes a pattern.
import { DUSTY_HZ, LANE_ORDER, defaultLaneToPad, nextCell, setBars, stylePattern, type StylePattern } from '../data/pattern';
import type { Cell, Kit, LaneId, PadSettings, Pattern, Sample, StyleId } from '../data/types';

/**
 * Humanize draws come from one seeded stream that starts afresh on every Play, so loop 1 of playback is
 * exactly the exported MIDI and the first bars are exactly the exported WAV loop (DECISIONS 2026-10-07).
 */
export const HUMANIZE_SEED = 1;

/** The one working groove row until projects arrive (v0.6 copies it into a project). */
export const WORKING_ID = 'groove';

export const STEPS_PER_BAR = 16;
export const BPM_MIN = 60;
export const BPM_MAX = 180;
export const SWING_MIN = 50;
export const SWING_MAX = 75;
export const DUSTY_MIN_HZ = 1000;
export const DUSTY_MAX_HZ = 20000;

type Lanes = Record<LaneId, Cell[]>;

/** The style's pattern as a working groove at `bars` bars, nothing edited. */
export function freshPattern(styleId: StyleId, bars: 1 | 2 = 1): Pattern {
  const base = setBars(stylePattern(styleId), bars);
  return {
    id: WORKING_ID, styleId, bars, bpm: base.bpm, swing: base.swing, humanize: base.humanize,
    dusty: base.dusty, dustyHz: DUSTY_HZ, lanes: base.lanes, laneToPad: {},
  };
}

/** Every lane, full length for the bar count; a missing lane reads as all off. */
export function lanesOf(p: Pattern): Lanes {
  const out = {} as Lanes;
  const len = p.bars * STEPS_PER_BAR;
  for (const lane of LANE_ORDER) {
    const cells = p.lanes[lane] ?? [];
    out[lane] = Array.from({ length: len }, (_, i) => cells[i] ?? '.');
  }
  return out;
}

const sameCells = (a: Lanes, b: Lanes) => LANE_ORDER.every((l) => a[l].join('') === b[l].join(''));

/** Which parts differ from the pattern's own style default. Bars and lane mapping are always yours, so they are not listed. */
export function editedParts(p: Pattern): { cells: boolean; bpm: boolean; swing: boolean; humanize: boolean; dusty: boolean } {
  const d = freshPattern(p.styleId, p.bars);
  return {
    cells: !sameCells(lanesOf(p), lanesOf(d)),
    bpm: p.bpm !== d.bpm,
    swing: p.swing !== d.swing,
    humanize: p.humanize !== d.humanize,
    dusty: p.dusty !== d.dusty || (p.dustyHz ?? DUSTY_HZ) !== DUSTY_HZ,
  };
}

/**
 * Style change (DECISIONS 2026-10-06): each part still at the old style's default takes the new style's
 * default; each part you edited stays. Bars and lane overrides always stay.
 */
export function switchStyle(p: Pattern, styleId: StyleId): Pattern {
  if (p.styleId === styleId) return p;
  const e = editedParts(p);
  const d = freshPattern(styleId, p.bars);
  return {
    ...p,
    styleId,
    lanes: e.cells ? p.lanes : d.lanes,
    bpm: e.bpm ? p.bpm : d.bpm,
    swing: e.swing ? p.swing : d.swing,
    humanize: e.humanize ? p.humanize : d.humanize,
    dusty: e.dusty ? p.dusty : d.dusty,
    dustyHz: e.dusty ? p.dustyHz : d.dustyHz,
  };
}

/** Taps a cell through its cycle (off, hit, accent, ghost; H also both rolls). */
export function tapCell(p: Pattern, lane: LaneId, step: number): Pattern {
  const lanes = lanesOf(p);
  const cells = [...lanes[lane]];
  cells[step] = nextCell(lane, cells[step] ?? '.');
  return { ...p, lanes: { ...lanes, [lane]: cells } };
}

/** 1 or 2 bars: growing copies bar 1 into bar 2, shrinking keeps bar 1 (SPEC F5, as `setBars`). */
export function withBars(p: Pattern, bars: 1 | 2): Pattern {
  if (p.bars === bars) return p;
  const lanes = lanesOf(p);
  const out = {} as Lanes;
  for (const lane of LANE_ORDER) {
    const bar1 = lanes[lane].slice(0, STEPS_PER_BAR);
    out[lane] = bars === 2 ? [...bar1, ...bar1] : bar1;
  }
  return { ...p, bars, lanes: out };
}

/** The IDs of your rim samples, for the R lane (SPEC F5 v0.5.1). */
export const rimIds = (samples?: readonly Sample[]): ReadonlySet<string> =>
  new Set((samples ?? []).filter((s) => s.type === 'rim' && s.deletedAt === undefined).map((s) => s.id));

/** The lane map in effect: the kit's role pads (R on the rim pad, P and B on their preferred perc pads), with your remaps on top. `rims`: rim sample IDs. */
export function effectiveLaneToPad(p: Pattern, kit?: Kit, rims?: ReadonlySet<string>): Partial<Record<LaneId, number>> {
  return { ...defaultLaneToPad(kit, rims), ...p.laneToPad };
}

/** The pattern the scheduler plays: the style's push and synth kit, your cells, tempo and switches. */
export function playable(p: Pattern, kit?: Kit, rims?: ReadonlySet<string>): StylePattern {
  const base = stylePattern(p.styleId);
  return {
    ...base, bars: p.bars, bpm: p.bpm, swing: p.swing, humanize: p.humanize, dusty: p.dusty,
    lanes: lanesOf(p), laneToPad: effectiveLaneToPad(p, kit, rims),
  };
}

export type LaneVoice = { kind: 'sample'; pad: number; sampleId: string } | { kind: 'synth'; pad?: number };

/** What a lane plays: its pad's sample, or the style's synthesised voice when the pad is empty or the kit is (SPEC F5 fallback). */
export function laneVoice(lane: LaneId, map: Partial<Record<LaneId, number>>, pads?: readonly PadSettings[]): LaneVoice {
  const pad = map[lane];
  const sampleId = pad === undefined ? undefined : pads?.[pad]?.sampleId;
  return pad !== undefined && sampleId ? { kind: 'sample', pad, sampleId } : { kind: 'synth', pad };
}

/** Lanes the grid shows: the ones the style uses, plus any lane you have put a hit on. */
export function shownLanes(p: Pattern): LaneId[] {
  const style = stylePattern(p.styleId).lanes;
  const lanes = lanesOf(p);
  return LANE_ORDER.filter((l) => style[l].some((c) => c !== '.') || lanes[l].some((c) => c !== '.'));
}

/** The straight 16th sounding at `now`, given the start of the loop it is in. Undefined before the loop starts. */
export function playheadStep(now: number, loopStart: number, bpm: number, bars: 1 | 2): number | undefined {
  if (now < loopStart) return undefined;
  const s16 = 60 / bpm / 4;
  const step = Math.floor((now - loopStart) / s16);
  const total = bars * STEPS_PER_BAR;
  return step < total ? step : total - 1;
}

/** "Soul boom bap pattern, 2 bars, humanize on" (DESIGN_SYSTEM §4). */
export function summaryLine(styleLabel: string, p: Pattern): string {
  return `${styleLabel} pattern, ${p.bars} ${p.bars === 1 ? 'bar' : 'bars'}, humanize ${p.humanize ? 'on' : 'off'}`;
}
